import { ErrorCodes } from 'constants/errors.constants';
import { useRef, useState, useEffect } from 'preact/hooks';
import { useCurrentShipment } from 'components/MerchantTrackingWidget/hooks/useCurrentShipment';
import { getFromAddressDetails } from './SearchDropOff.utils';
import { SearchDropOffValues } from './SearchDropOff.interfaces';

interface UseSearchDropOffParams {
  onSearch: (values: SearchDropOffValues) => void;
}

export const useSearchDropOff = ({ onSearch }: UseSearchDropOffParams) => {
  const { shipment } = useCurrentShipment();
  const fromAddressDetails = getFromAddressDetails(shipment);
  const hasFromAddressDetails =
    fromAddressDetails?.postalCode && fromAddressDetails?.countryCode;

  const [values, setValues] = useState({
    postalCode: fromAddressDetails?.postalCode || '',
    countryCode: fromAddressDetails?.countryCode || '',
  });
  const [errors, setErrors] = useState({ postalCode: '', countryCode: '' });

  const isSubmitted = useRef(false);

  const onValidate = (name: string, value: string) => {
    const required = value.trim() ? '' : ErrorCodes.FormErrorEmptyField;

    const validation = {
      countryCode: required,
      postalCode: required,
    };

    return validation[name];
  };

  const onChangePostalCode = (evt: Event) => {
    const { value } = evt.target as HTMLInputElement;

    if (isSubmitted.current) {
      const error = onValidate('postalCode', value);

      if (error !== errors.postalCode) {
        setErrors({ ...errors, postalCode: error });
      }
    }

    setValues({ ...values, postalCode: value });
  };

  const onFormatPostalCode = (evt: Event) => {
    const { value } = evt.target as HTMLInputElement;
    setValues({ ...values, postalCode: value.trim() });
  };

  const onChangeCountryCode = (value: string) => {
    if (isSubmitted.current) {
      const error = onValidate('countryCode', value);

      if (error !== errors.countryCode) {
        setErrors({ ...errors, countryCode: error });
      }
    }

    setValues({ ...values, countryCode: value });
  };

  const onSearchDropOff = async () => {
    if (!isSubmitted.current) {
      isSubmitted.current = true;
    }

    let isValid = true;

    Object.keys(values).forEach((key) => {
      const error = onValidate(key, values[key]);
      if (error) {
        isValid = false;
        setErrors((errors) => ({ ...errors, [key]: error }));
      }
    });

    if (!isValid) return;

    onSearch(values);
  };

  // Auto-trigger search when has from_address details
  useEffect(() => {
    if (hasFromAddressDetails) {
      onSearchDropOff();
    }
  }, [hasFromAddressDetails]);

  return {
    values,
    errors,
    onChangePostalCode,
    onFormatPostalCode,
    onChangeCountryCode,
    onSearchDropOff,
  };
};
